import { useToggle } from "react-use";
import { Alert, Button, Flex, Modal, Typography, message } from "@payping/web-components";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { RHFInputNumber } from "@/shared/components";
import useTranslator from "@/shared/translate/translator";
import {
  useConfirmWithdraw,
  useCreateRecharge,
  useCreateWithdraw,
  useGetRefundList,
  useGetWalletBalance,
} from "@/refund/use-cases";
import { FC, useState } from "react";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import Balance from "../balance";

interface FormValues {
  amount?: number;
}

interface WithdrawFormValues {
  amount?: number;
}

const Deposit: FC = () => {
  const { t: generalT } = useTranslator();
  const { t } = useTranslator("refund.wallet");
  const walletBalance = useGetWalletBalance();
  const refundList = useGetRefundList({
    PageNumber: 1,
    PageSize: 10,
    States: [],
  });

  const minAmount = String(process.env.REACT_APP_DOMAIN).includes("payping.ir") ? 50000 : 1000;
  const suggestedAmounts = [2000000, 5000000, 10000000] as const satisfies readonly number[];

  const { control, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      amount: minAmount,
    },
  });

  const { control: withdrawControl, handleSubmit: handleWithdrawSubmit } = useForm<WithdrawFormValues>({
    defaultValues: {
      amount: minAmount,
    },
  });

  const setAmount = (amount: number) => () => {
    setValue("amount", amount);
  };

  const createRecharge = useCreateRecharge();
  const createWithdraw = useCreateWithdraw();
  const confirmWithdraw = useConfirmWithdraw();
  const [openModal, toggleModal] = useToggle(false);
  const [openWithdrawModal, toggleWithdrawModal] = useToggle(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [withdrawCode, setWithdrawCode] = useState<string | undefined>();
  const [withdrawAmount, setWithdrawAmount] = useState<number | undefined>();
  const [withdrawIban, setWithdrawIban] = useState<string | undefined>();

  const rechargeHandler: SubmitHandler<FormValues> = async (data) => {
    if (data.amount)
      try {
        const response = await createRecharge.mutateAsync({ amount: data.amount });
        if (response.data.paymentLink) {
          // eslint-disable-next-line react-hooks/immutability
          window.location.href = response.data.paymentLink;
        }
      } catch {
        //
      }
  };

  const withdrawHandler: SubmitHandler<WithdrawFormValues> = async (data) => {
    if (data.amount) {
      try {
        const response = await createWithdraw.mutateAsync({
          amount: data.amount,
        });
        if (response.data.code) {
          if (response.data?.iban) setWithdrawIban(response.data?.iban);
          setWithdrawCode(response.data.code);
          setWithdrawAmount(data.amount);
          toggleWithdrawModal();
          setOpenConfirmModal(true);
        }
      } catch {
        // handle error
      }
    }
  };

  return (
    <>
      <Modal
        open={openConfirmModal}
        onOk={async () => {
          if (!withdrawCode) return;
          try {
            await confirmWithdraw.mutateAsync(withdrawCode);
            message.success({ content: t("withdrawSuccess") });
            await Promise.all([walletBalance.refetch(), refundList.refetch()]);
            setOpenConfirmModal(false);
          } catch {
            // handle error
          }
        }}
        onCancel={() => setOpenConfirmModal(false)}
        title={<p className="tw:font-medium">{t("withdraw")}</p>}
        footer={[
          <Button key="cancel" block onClick={() => setOpenConfirmModal(false)}>
            {generalT("common.cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            block
            onClick={async () => {
              if (!withdrawCode) return;
              try {
                await confirmWithdraw.mutateAsync(withdrawCode);
                message.success({ content: t("withdrawSuccess") });
                await Promise.all([walletBalance.refetch(), refundList.refetch()]);
                setOpenConfirmModal(false);
              } catch {
                // handle error
              }
            }}
            loading={confirmWithdraw.isPending}
            disabled={confirmWithdraw.isPending}
          >
            {t("confirm")}
          </Button>,
        ]}
      >
        <Flex vertical gap={8} className="tw:w-full">
          <Alert
            title={t("confirmWithdrawModal.alertMessage", {
              amount: formatLocalizedNumber({ value: withdrawAmount || 0, options: { maximumFractionDigits: 2 } }),
            })}
            type="warning"
          />
          <Flex vertical gap={4} className="tw:px-2!">
            <Flex align="center">
              <Typography.Text>{t("withdrawAmount")}:&nbsp;</Typography.Text>
              <Typography.Text>
                {formatLocalizedNumber({ value: withdrawAmount || 0, options: { maximumFractionDigits: 2 } })}
              </Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>

            {withdrawIban && (
              <Typography.Text>
                {t("iban")}: {withdrawIban}
              </Typography.Text>
            )}
          </Flex>
        </Flex>
      </Modal>
      <Button onClick={toggleWithdrawModal} type="default">
        {t("withdraw")}
      </Button>
      <Button onClick={toggleModal} type="primary">
        {t("deposit.title")}
      </Button>

      <Modal
        open={openModal}
        onOk={handleSubmit(rechargeHandler)}
        onCancel={toggleModal}
        title={<p className="tw:font-medium">{t("deposit.modal_title")}</p>}
        footer={[
          <Button
            key="submit"
            type="primary"
            block
            onClick={handleSubmit(rechargeHandler)}
            disabled={createRecharge.isPending}
            loading={createRecharge.isPending}
          >
            {t("deposit.modal_submit")}
          </Button>,
        ]}
      >
        <Flex vertical className="tw:w-full">
          <div className="tw:mb-3! tw:rounded-sm tw:border tw:border-solid tw:border-color-border tw:bg-color-bg-layout tw:p-8!">
            <Balance titleClassName="tw:font-sm!" amountClassName="tw:font-base!" currencyClassName="tw:font-xs!" />
          </div>
          <RHFInputNumber
            label={t("deposit.label_amount")}
            name="amount"
            // @ts-ignore wrong form type
            control={control}
            rules={{
              required: generalT("common.validations.required", { name: t("deposit.amount") }),
              min: {
                value: minAmount,
                message: generalT("common.validations.min", { name: t("deposit.amount"), min: minAmount }),
              },
            }}
            disabled={createRecharge.isPending}
            className="tw:w-full!"
            suffix={<CuToman width={24} height={24} />}
            controls={false}
            step={1000}
          />

          <Typography.Text className="tw:mb-[8px]!">{t("deposit.suggested_amount")}</Typography.Text>
          <Flex className="tw:w-full" gap="16px">
            {suggestedAmounts.map((item) => (
              <Button key={item} onClick={() => setAmount(item)} disabled={createRecharge.isPending}>
                <Flex align="center">
                  {formatLocalizedNumber({ value: item, options: { maximumFractionDigits: 2 } })}{" "}
                  <CuToman width={24} height={24} />
                </Flex>
              </Button>
            ))}
          </Flex>
        </Flex>
      </Modal>

      <Modal
        open={openWithdrawModal}
        onOk={handleWithdrawSubmit(withdrawHandler)}
        onCancel={toggleWithdrawModal}
        title={<p className="tw:font-medium">{t("withdraw")}</p>}
        footer={[
          <Button
            key="submit"
            type="primary"
            block
            onClick={handleWithdrawSubmit(withdrawHandler)}
            disabled={createWithdraw.isPending}
            loading={createWithdraw.isPending}
          >
            {t("withdraw")}
          </Button>,
        ]}
      >
        <Flex vertical className="tw:w-full">
          <div className="tw:mb-3! tw:rounded tw:border tw:border-solid tw:border-color-border tw:bg-color-bg-layout tw:p-8!">
            <Balance titleClassName="tw:text-sm!" amountClassName="tw:text-base!" currencyClassName="tw:text-xs!" />
          </div>

          <RHFInputNumber
            label={t("deposit.amount")}
            name="amount"
            // @ts-ignore wrong form type
            control={withdrawControl}
            rules={{
              required: generalT("common.validations.required", { name: t("deposit.amount") }),
              min: {
                value: minAmount,
                message: generalT("common.validations.min", { name: t("deposit.amount"), min: minAmount }),
              },
            }}
            disabled={createWithdraw.isPending}
            className="tw:w-full!"
            suffix={<CuToman width={24} height={24} />}
            controls={false}
            step={1000}
          />
        </Flex>
      </Modal>
    </>
  );
};
export default Deposit;
